import { makeAutoObservable, runInAction } from "mobx";
import ReturnMethodsService from "../../../services/api/return-methods-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import { IReturnLabelSettings, IReturnMethodAddress } from "../return-method-types";
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import { IShippingLabelSettingsChanges } from "./shipping-label-settings-store";
import { AUTO_SELECT_OPTION, OVERRIDDEN_BY_CONNECTION } from "../constants";
import { IChangeable } from "../../../stores/types";
import { CarrierOptionsCacheStore } from "./carrier-options-cache-store";
import { getNullableChangesOrModel } from "../../../models/model-types-methods";

export default class DefaultCarrierSelectorStore implements IChangeable<IReturnLabelSettings> {
    private _changes: IShippingLabelSettingsChanges = {};
    private _isCarriersListLoading: boolean = false;
    private _isServicesListLoading: boolean = false;
    private _isPackagesListLoading: boolean = false;

    constructor(
        private _getModel: () => IReturnLabelSettings | undefined,
        private _getAddress: () => IReturnMethodAddress | undefined,
        private _setUseCustomPackageIfNecessary: () => void,
        private getShippingConnectionId: () => number | undefined,
        private getUseForwardLabels: () => boolean,
        private getIsCoverageConnection: () => boolean,
        private carrierOptionsCacheStore: CarrierOptionsCacheStore
    ) {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get isCarriersListLoading() { return this._isCarriersListLoading; }
    public get isServicesListLoading() { return this._isServicesListLoading; }
    public get isPackagesListLoading() { return this._isPackagesListLoading; }

    private get _carriersList(): IOption[] {
        return this.carrierOptionsCacheStore.getCarriersOptions(this.getShippingConnectionId());
    }
    public get carriersList() { return this._carriersList; }
    
    private get _serviceLevelList() {
        return this.carrierOptionsCacheStore.getServicesOptions(this.getShippingConnectionId(), this.carrierCode);
    }
    public get serviceLevelList() { return this._serviceLevelList; }
    
    private get _packagesList() { 
        return this.carrierOptionsCacheStore.getPackagesOptions(this.getShippingConnectionId(), this.carrierCode, this.serviceCode); 
    }
    public get packagesList() { return this._packagesList; }
    
    
    public get carrierCode(): string | null | undefined {
        return getNullableChangesOrModel(this._changes.carrier_code, this._getModel()?.carrier_code);
    }
    public get serviceCode(): string | null | undefined {
        return getNullableChangesOrModel(this._changes.service_code, this._getModel()?.service_code);
    }
    public get packageCode(): string | null | undefined {
        return getNullableChangesOrModel(this._changes.package_code, this._getModel()?.package_code);
    }

    private get returnMethodsService(): ReturnMethodsService {
        return rootContainer.get<ReturnMethodsService>(TYPE.ReturnMethodsService);
    }

    public isLoading(): boolean {
        return this._isCarriersListLoading || this._isServicesListLoading || this._isPackagesListLoading;
    }

    public async load() {
        if (this.getShippingConnectionId()) {
            this.getCarriersList();
        }

        if (this.carrierCode) {
            this.getServiceLevelList();
        }

        if (this.serviceCode) {
            this.getPackagesList();
        }
    }

    public async getCarriersList() {
        this._isCarriersListLoading = true;
        try {

            const connectionId = this.getShippingConnectionId()!;
            const useForwardLabels = this.getUseForwardLabels();
            const address = this._getAddress();

            const carriersList = await this.returnMethodsService.getCarriersList(connectionId, useForwardLabels, address);

            runInAction(() => {
                this.carrierOptionsCacheStore.setCarriersOptions(connectionId, carriersList);
                this._isCarriersListLoading = false;
            });
        }
        catch (e) {
            console.error(e);
            runInAction(() => {
                this._isCarriersListLoading = false;
            });
        }
    }

    public async getServiceLevelList() {
        this._isServicesListLoading = true;
        try {
            if (this.carrierCode) {
                const connectionId = this.getShippingConnectionId()!;
                const carrierCode = this.carrierCode;
                const useForwardLabels = this.getUseForwardLabels();
                const address = this._getAddress();

                const serviceLevelList = await this.returnMethodsService.getServiceLevelList(connectionId, carrierCode, useForwardLabels, address);

                runInAction(() => {
                    this.carrierOptionsCacheStore.setServicesOptions(connectionId, carrierCode, serviceLevelList);                    
                    this._isServicesListLoading = false;
                });
            }
        }
        catch {
            runInAction(() => {
                this._isServicesListLoading = false;
            });
        }
    }

    public async getPackagesList() {
        this._isPackagesListLoading = true;
        try {
            if (this.carrierCode && this.serviceCode) {
                
                const connectionId = this.getShippingConnectionId()!;
                const carrierCode = this.carrierCode;
                const serviceCode = this.serviceCode;

                const packagesList = await (this.returnMethodsService.getPackagesList(connectionId, carrierCode, serviceCode));
                runInAction(() => {
                    this._isPackagesListLoading = false;
                    this.carrierOptionsCacheStore.setPackagesOptions(connectionId, carrierCode, serviceCode, packagesList);
                });
            }
        }
        catch {
            runInAction(() => {
                this._isPackagesListLoading = false;
            });
        }
    }

    public getUpdatedModel(): IReturnLabelSettings {
        return {
            carrier_code: this.carrierCode,
            service_code: this.serviceCode,
            package_code: this.packageCode,
        } as IReturnLabelSettings;
    }

    public shouldSetDefaultPackageCode(): boolean {
        return this._packagesList?.length === 1 || this.serviceCode === AUTO_SELECT_OPTION;
    }

    public hasAutoSelection(): boolean {
        return this.carrierCode === AUTO_SELECT_OPTION || this.serviceCode === AUTO_SELECT_OPTION;
    }

    public setPackageCode(code: string) {
        if (this.getIsCoverageConnection()) {
            return;
        }
        this._changes.package_code = code;
    }

    private _setDefaultPackageCode() {
        /** if package list contains only one option - auto select it */
        if (this.shouldSetDefaultPackageCode()) {
            this.setPackageCode((this._packagesList?.[0]?.value as string) ?? 'N/A');
        }
    }

    public async setServiceCode(code: string) {
        if (this.getIsCoverageConnection()) {
            return;
        }

        this._changes.service_code = code;

        this._changes.package_code = null;
        this._setUseCustomPackageIfNecessary();
        await this.getPackagesList();
        runInAction(() => {
            this._setDefaultPackageCode();
        });
    }

    public async setCarrierCode(code: string) {
        if (this.getIsCoverageConnection()) {
            return;
        }

        this._changes.carrier_code = code;

        /** clear previos selections */
        this._changes.service_code = null;
        this._changes.package_code = null;

        await this.getServiceLevelList();
        runInAction(() => {
            /** set default service code if needed */
            this._setDefaultServiceCode(this._serviceLevelList as IOption[]);
        });

        /** set use custom package toggle according to selected carrire and service */
        this._setUseCustomPackageIfNecessary();
    }

    private _setDefaultServiceCode(serviceLevelList: IOption[]) {
        /** if there's only one option and it's different from the existing code from DB - select it*/
        if (serviceLevelList.length === 1 && serviceLevelList[0].value !== this.serviceCode) {
            this.setServiceCode(serviceLevelList[0].value as string);
            return;
        }

        /** if there's auto select cheapest option - auto select it*/
        const autoSelectCheapest = serviceLevelList.find(service => service.value === AUTO_SELECT_OPTION);
        if (autoSelectCheapest) {
            this.setServiceCode(autoSelectCheapest.value as string);
        }
    }

    private _setDefaultCarrierCode(carriersList: IOption[]) {
        /** if there's only one option and it's different from the existing code from DB- select it*/
        if (carriersList.length === 1 && carriersList[0].value !== this.carrierCode) {
            this.setCarrierCode(carriersList[0].value as string);
            return;
        }

        /** if there's auto select cheapest option - auto select it */
        const autoSelectCheapest = carriersList.find(carrier => carrier.value === AUTO_SELECT_OPTION);
        if (autoSelectCheapest) {
            this.setCarrierCode(autoSelectCheapest.value as string);
        }
    }

    public async setDefaultCarrierCode() {
        await this.getCarriersList();
        runInAction(() => {
            /** set default carrier */
            this._setDefaultCarrierCode(this._carriersList as IOption[]);
        });
    }

    public hasChanges(): boolean {
        return Object.keys(this._changes).length > 0;
    }

    public discardChanges(): void {
        this._changes = {};
    }

    public resetSelections(): void {
        this._changes.carrier_code = null;
        this._changes.service_code = null;
        this._changes.package_code = null;
    }

    public setAllOverriddenByConnection() {
        this._changes.carrier_code = OVERRIDDEN_BY_CONNECTION;
        this._changes.service_code = OVERRIDDEN_BY_CONNECTION;
        this._changes.package_code = OVERRIDDEN_BY_CONNECTION;
    }
}